import React, { useState, useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import { useSession } from '../Utils/SessionContext';
import usePopupClose from '../Utils/usePopupClose';

const UploadResume = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  candidateId = null,
  talentId = null,
  jobId = null,
  context = 'candidate' 
}) => {
  const { user } = useSession();
  
  const [formData, setFormData] = useState({
    additionalLink: '',
    generateVsResumeEvaluation: false
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const fileInputRef = useRef(null);


  // Reset form when closed
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        additionalLink: '',
        generateVsResumeEvaluation: false
      });
      setResumeFile(null);
      setErrors({});
      setSubmitError(null);
      setIsSubmitting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setErrors({
          ...errors,
          resume: 'Only PDF files are allowed'
        });
        setResumeFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setResumeFile(file);
        if (errors.resume) {
          const newErrors = { ...errors };
          delete newErrors.resume;
          setErrors(newErrors);
        }
      }
    } else {
      setResumeFile(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate resume file (required)
    if (!resumeFile) {
      newErrors.resume = 'Resume file is required';
    }
    
    // Additional link is optional, but if provided, validate it's a URL
    if (formData.additionalLink && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i.test(formData.additionalLink)) {
      newErrors.additionalLink = 'Please enter a valid URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        // Check if user exists
        if (!user) {
          throw new Error('User session not available. Please log in again.');
        }
        
        // Create FormData object for file upload
        const formDataToSubmit = new FormData();
        formDataToSubmit.append('resume', resumeFile);
        
        // Ensure the additional link starts with https://
        let link = formData.additionalLink || '';
        if (link && !link.startsWith('http://') && !link.startsWith('https://')) {
          link = 'https://' + link;
        }
        formDataToSubmit.append('link', link);
        
        // Add generateVsResumeEvaluation flag
        formDataToSubmit.append('generateVsResumeEvaluation', formData.generateVsResumeEvaluation);
        
        // Add IDs based on context
        if (context === 'candidate') {
          if (candidateId) formDataToSubmit.append('candidate_id', candidateId);
          if (talentId) formDataToSubmit.append('talent_id', talentId);
          if (jobId) formDataToSubmit.append('job_id', jobId);
          
          // Add action_data JSON object for candidate
          formDataToSubmit.append('action_data', JSON.stringify({ action: "create-job-profile-fit" }));
        } else {
          // For talent context
          if (talentId) formDataToSubmit.append('talent_id', talentId);
          
          // Add action_data JSON object for talent
          formDataToSubmit.append('action_data', JSON.stringify({ action: "generate-talent-profile-assessment" }));
        }

        // Send the form data to the server
        const response = await fetch(API_ENDPOINTS.UPLOAD_RESUME, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.access_token}`
          },
          body: formDataToSubmit,
        });
        
        if (!response.ok) {
          const error = new Error(`Error: ${response.status} ${response.statusText}`);
          error.status = response.status;
          throw error;
        }
        
        const responseData = await response.json();
        console.log('Resume submission response:', responseData);
        
        // Notify the parent component that submission was successful
        if (onSubmit) {
          onSubmit(responseData);
        }
        
        onClose(); // Close popup on success
      } catch (error) {
        console.error('Error uploading resume:', error);
        console.log('Error status code:', error.status);
        
        // If it's a 401 error, it's an expired session
        if (error.status === 401) {
          console.log('Session expired or unauthorized.');
          setSubmitError('Your session has expired. Please log in again.');
          return;
        }
        
        setSubmitError(error.message || 'Failed to upload resume. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Use the popup close hook to handle ESC key press and clicks outside
  const modalRef = usePopupClose(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">
            {context === 'candidate' ? 'Upload Candidate Resume' : 'Upload Talent Resume'}
          </h2>
          <p className="text-gray-600 mt-1">
            {context === 'candidate' 
              ? 'Upload a resume and additional information for this candidate. This will generate the Candidate Profile and Job-Profile Fit.'
              : 'Upload a resume and additional information for this talent. This will generate the Talent Profile and Talent Profile Assessment.'}
          </p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          {/* Resume Upload */}
          <div>
            <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-1">
              Resume (PDF) <span className="text-red-500">*</span>
            </label>
            <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${
              errors.resume ? 'border-red-300' : 'border-gray-300'
            }`}>
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="resume-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="resume-upload"
                      name="resume-upload"
                      type="file"
                      className="sr-only"
                      accept=".pdf"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PDF up to 10MB</p>
                {resumeFile && (
                  <p className="text-sm text-green-600">
                    Selected: {resumeFile.name}
                  </p>
                )}
              </div>
            </div>
            {errors.resume && (
              <p className="mt-1 text-sm text-red-600">{errors.resume}</p>
            )}
          </div>
          
          {/* Additional Link */}
          <div className="mb-4">
            <label htmlFor="additionalLink" className="block text-sm font-medium text-gray-700 mb-1">
              Additional Link (e.g. LinkedIn, Portfolio, GitHub)
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                id="additionalLink"
                name="additionalLink"
                value={formData.additionalLink}
                onChange={handleChange}
                className={`block w-full pl-10 rounded-md sm:text-sm p-2 border ${
                  errors.additionalLink ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="https://example.com"
              />
            </div>
            {errors.additionalLink && (
              <p className="mt-1 text-sm text-red-600">{errors.additionalLink}</p>
            )}
          </div>
          
          {/* Generate VsResume Evaluation */}
          <div className="mb-4 hidden">
            <div className="flex items-center">
              <input
                id="generateVsResumeEvaluation"
                name="generateVsResumeEvaluation"
                type="checkbox"
                checked={formData.generateVsResumeEvaluation}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="generateVsResumeEvaluation" className="ml-2 block text-sm font-medium text-gray-700">
                Generate VSResume Evaluation For Talent
              </label>
            </div>
          </div>
          
          {/* Error Message */}
          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}
          
          {/* Warning Message */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-base text-l text-yellow-700">
                  Processing takes ~15 minutes, return later for results.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </span>
              ) : (
                'Upload'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadResume;
